import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBlocker } from "react-router-dom";

export const NavigationBlockerContext = createContext(null);

const NavigationBlockerProvider = ({ children }) => {
  const [blockers, setBlockers] = useState(new Map());

  const registerBlocker = useCallback((id, shouldBlock) => {
    setBlockers((prev) => {
      if (prev.has(id)) {
        return prev;
      }
      const next = new Map(prev);
      next.set(id, { shouldBlock: !!shouldBlock });
      return next;
    });
  }, []);

  const unregisterBlocker = useCallback((id) => {
    setBlockers((prev) => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const updateBlocker = useCallback((id, shouldBlock) => {
    setBlockers((prev) => {
      const existing = prev.get(id);
      if (!existing || existing.shouldBlock === !!shouldBlock) {
        return prev;
      }
      const next = new Map(prev);
      next.set(id, { shouldBlock: !!shouldBlock });
      return next;
    });
  }, []);

  const activeBlockerId = useMemo(() => {
    const entries = Array.from(blockers.entries());
    for (let i = entries.length - 1; i >= 0; i -= 1) {
      if (entries[i][1].shouldBlock) {
        return entries[i][0];
      }
    }
    return null;
  }, [blockers]);

  const shouldBlock = activeBlockerId !== null;
  const blocker = useBlocker(shouldBlock);

  const value = useMemo(
    () => ({
      registerBlocker,
      unregisterBlocker,
      updateBlocker,
      blocker,
      activeBlockerId,
    }),
    [registerBlocker, unregisterBlocker, updateBlocker, blocker, activeBlockerId]
  );

  return (
    <NavigationBlockerContext.Provider value={value}>
      {children}
    </NavigationBlockerContext.Provider>
  );
};

export default NavigationBlockerProvider;
